// The emoji picker (PLAN-2026-10-27-more-fun-in-chat C1): the site's emoji
// (`CHAT_EMOJI_GROUPS`, `@fsp/domain`) in their groups, a search, and the ones
// this device picked last. The chat box opens it to type one, and a message's
// bar opens it to react with one; the set is the server's, so whatever it
// offers a reaction may be.
//
// RECENT IS THIS DEVICE'S, in the browser's storage: a convenience, never
// state anybody else reads, so a browser that will not keep it starts with
// *Game day* every time and nothing breaks.

import { useState, type KeyboardEvent } from 'react';
import {
  CHAT_EMOJI_GROUPS,
  chatEmoji,
  searchEmoji,
  type ChatEmoji,
  type ChatEmojiGroup,
} from '@fsp/domain';
import { IconButton, TextField } from './ui';

const RECENT_KEY = 'fsp.chat.recentEmoji';
const RECENT_MAX = 24;

function readRecent(): string[] {
  try {
    const raw = window.localStorage.getItem(RECENT_KEY);
    const list: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(list)
      ? list.filter((c): c is string => typeof c === 'string' && chatEmoji(c) !== undefined)
      : [];
  } catch {
    return [];
  }
}

function remember(char: string): string[] {
  const next = [char, ...readRecent().filter((c) => c !== char)].slice(0, RECENT_MAX);
  try {
    window.localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    // Not kept: the picker still works, without a Recent row next time.
  }
  return next;
}

/** A pointer that is a mouse: the search takes the keyboard. A phone's would cover the picker. */
export function finePointer(): boolean {
  return typeof window !== 'undefined' && window.matchMedia?.('(pointer: fine)').matches === true;
}

/** How an emoji is said: its name, in words. */
export function emojiLabel(e: ChatEmoji): string {
  return e.name.replace(/_/g, ' ');
}

export function EmojiPicker({
  title,
  onPick,
  onClose,
  exclude,
}: {
  /** What it is for, said to a screen reader: "Emoji", "React to Dave's message". */
  title: string;
  onPick: (char: string) => void;
  onClose: () => void;
  /** Emoji not to offer: a message's reactions already there. */
  exclude?: ReadonlySet<string>;
}) {
  const [recent, setRecent] = useState<string[]>(readRecent);
  const [tab, setTab] = useState<string>(() => (readRecent().length > 0 ? 'recent' : 'game-day'));
  const [query, setQuery] = useState('');
  const groups: ChatEmojiGroup[] = [
    ...(recent.length > 0
      ? [
          {
            id: 'recent',
            label: 'Recent',
            icon: '🕘',
            emoji: recent.map(chatEmoji).filter((e): e is ChatEmoji => e !== undefined),
          },
        ]
      : []),
    ...CHAT_EMOJI_GROUPS,
  ];
  const current = groups.find((g) => g.id === tab) ?? groups[0]!;
  const found = query.trim() === '' ? null : searchEmoji(query);
  const shown = (found ?? current.emoji).filter((e) => !exclude?.has(e.char));

  const pick = (e: ChatEmoji) => {
    setRecent(remember(e.char));
    onPick(e.char);
  };
  const keys = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Escape') return;
    e.stopPropagation();
    onClose();
  };

  return (
    <div className="fsp-emoji" role="dialog" aria-label={title} onKeyDown={keys}>
      <div className="fsp-emoji-head">
        <TextField
          label="Find an emoji"
          hideLabel
          grow
          icon="search"
          placeholder="Find an emoji"
          value={query}
          onChange={setQuery}
          autoFocus={finePointer()}
        />
        <IconButton icon="close" label="Close emoji" variant="quiet" onClick={onClose} />
      </div>
      {found === null ? (
        <div className="fsp-emoji-tabs" role="tablist" aria-label="Emoji groups">
          {groups.map((g) => (
            <button
              key={g.id}
              type="button"
              role="tab"
              className="fsp-emoji-tab"
              aria-selected={g.id === current.id}
              aria-label={g.label}
              title={g.label}
              onClick={() => setTab(g.id)}
            >
              {g.icon}
            </button>
          ))}
        </div>
      ) : null}
      <div
        className="fsp-emoji-grid"
        role="group"
        aria-label={found === null ? current.label : 'Found'}
      >
        {shown.length === 0 ? (
          <p className="fsp-emoji-none">
            {found === null ? 'All of these are on it already.' : 'No emoji by that name.'}
          </p>
        ) : (
          shown.map((e) => (
            <button
              key={e.char}
              type="button"
              className="fsp-emoji-cell"
              aria-label={emojiLabel(e)}
              title={`:${e.name}:`}
              onClick={() => pick(e)}
            >
              {e.char}
            </button>
          ))
        )}
      </div>
    </div>
  );
}
